import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { CommonTablePaginatedResponse } from '@routes/sidebar/admin-center/___shared___/types.ts';
import type {
    CreateVesselEquipmentMakerRequest,
    UpdateVesselEquipmentMakerRequest,
    VesselEquipmentMaker,
    VesselEquipmentMakerResponse,
    VesselEquipmentMakersRequest,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/api/vessel-equipment-makers.api.ts';
import {
    createApiVesselEquipmentMaker,
    getApiVesselEquipmentMakers,
    updateApiVesselEquipmentMaker,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/api/vessel-equipment-makers.api.ts';
import { extractError } from '@utils/slice.ts';

export interface VesselEquipmentMakersError {
    message?: string;
    [k: string]: any;
}

export interface VesselEquipmentMakersState {
    error: VesselEquipmentMakersError | null;

    commonVesselEquipmentMakers: VesselEquipmentMakerResponse | null; // use for common data just in case
    vesselEquipmentMakers: VesselEquipmentMaker[];
    pagination: CommonTablePaginatedResponse;
    updatedVesselEquipmentMaker: VesselEquipmentMaker | null;
    currentGetRequestId: string | null;

    isGetVesselEqMakersPending: boolean;
    isCreateVesselEqMakerPending: boolean;
    isUpdateVesselEqMakerPending: boolean;
}

const initialState: VesselEquipmentMakersState = {
    error: null,

    commonVesselEquipmentMakers: null,
    vesselEquipmentMakers: [],
    updatedVesselEquipmentMaker: null,
    currentGetRequestId: null,

    pagination: {
        pageNumber: 0,
        pageSize: 0,
        totalPages: 0,
        totalResults: 0,
        maxPageSize: 0,
    },

    isGetVesselEqMakersPending: false,
    isCreateVesselEqMakerPending: false,
    isUpdateVesselEqMakerPending: false,
};

export const getVesselEquipmentMakers = createAsyncThunk<
    VesselEquipmentMakerResponse,
    { clientId: number; tenantAlias: string; payload: VesselEquipmentMakersRequest },
    { rejectValue: VesselEquipmentMakersError }
>(
    'adminCenterVesselEquipmentMakers/getVesselEquipmentMakers',
    ({ clientId, tenantAlias, payload }, { rejectWithValue }) =>
        getApiVesselEquipmentMakers(clientId, tenantAlias, payload)
            .then((res) => res)
            .catch((err) => rejectWithValue(err)),
);

export const createVesselEquipmentMaker = createAsyncThunk<
    VesselEquipmentMaker,
    { clientId: number; tenantAlias: string; payload: CreateVesselEquipmentMakerRequest },
    { rejectValue: VesselEquipmentMakersError }
>(
    'adminCenterVesselEquipmentMakers/createVesselEquipmentMaker',
    ({ clientId, tenantAlias, payload }, { rejectWithValue }) =>
        createApiVesselEquipmentMaker(clientId, tenantAlias, payload)
            .then((res) => res)
            .catch((err) => rejectWithValue(err)),
);

export const updateVesselEquipmentMaker = createAsyncThunk<
    VesselEquipmentMaker,
    { clientId: number; tenantAlias: string; id: number; payload: UpdateVesselEquipmentMakerRequest },
    { rejectValue: VesselEquipmentMakersError }
>(
    'adminCenterVesselEquipmentMakers/updateVesselEquipmentMaker',
    ({ clientId, tenantAlias, id, payload }, { rejectWithValue }) =>
        updateApiVesselEquipmentMaker(clientId, tenantAlias, id, payload)
            .then((res) => res)
            .catch((err) => rejectWithValue(err)),
);

const vesselEquipmentMakersSlice = createSlice({
    name: 'adminCenterVesselEquipmentMakers',
    initialState,
    reducers: {
        resetVesselEquipmentMakersState: () => initialState,
        cachedVesselEquipmentMakers(state) {
            if (state.commonVesselEquipmentMakers) {
                state.vesselEquipmentMakers = state.commonVesselEquipmentMakers.data ?? [];
                state.pagination = state.commonVesselEquipmentMakers.pagination;
            }
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getVesselEquipmentMakers.pending, (state, action) => {
            state.isGetVesselEqMakersPending = true;
            state.error = null;
            state.currentGetRequestId = action.meta.requestId;
        });
        builder.addCase(getVesselEquipmentMakers.fulfilled, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetVesselEqMakersPending = false;
            state.currentGetRequestId = null;
            const { data, pagination } = action.payload;

            const requestPayload = action.meta.arg.payload;
            const textSearch = requestPayload?.textSearch ?? '';

            const isBaseLoad = !textSearch.trim();

            if (isBaseLoad) {
                state.commonVesselEquipmentMakers = action.payload;
            }

            state.vesselEquipmentMakers = data ?? [];
            state.pagination = pagination;
        });
        builder.addCase(getVesselEquipmentMakers.rejected, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetVesselEqMakersPending = false;
            state.currentGetRequestId = null;
            state.error = extractError(action);
        });

        builder.addCase(createVesselEquipmentMaker.pending, (state) => {
            state.isCreateVesselEqMakerPending = true;
            state.error = null;
        });
        builder.addCase(createVesselEquipmentMaker.fulfilled, (state, action) => {
            state.isCreateVesselEqMakerPending = false;
            state.updatedVesselEquipmentMaker = action.payload;
        });
        builder.addCase(createVesselEquipmentMaker.rejected, (state, action) => {
            state.isCreateVesselEqMakerPending = false;
            state.error = extractError(action);
        });

        builder.addCase(updateVesselEquipmentMaker.pending, (state) => {
            state.isUpdateVesselEqMakerPending = true;
            state.error = null;
        });
        builder.addCase(updateVesselEquipmentMaker.fulfilled, (state, action) => {
            state.isUpdateVesselEqMakerPending = false;
            state.updatedVesselEquipmentMaker = action.payload;
        });
        builder.addCase(updateVesselEquipmentMaker.rejected, (state, action) => {
            state.isUpdateVesselEqMakerPending = false;
            state.error = extractError(action);
        });
    },
});

export const { resetVesselEquipmentMakersState, cachedVesselEquipmentMakers } = vesselEquipmentMakersSlice.actions;
export default vesselEquipmentMakersSlice.reducer;
